import React, { useState, useEffect } from 'react';
import { FiDollarSign, FiTrendingUp, FiDownload, FiPlus, FiFilter, FiCalendar, FiUsers, FiFileText, FiX, FiAlertTriangle, FiEye, FiBarChart, FiMail } from 'react-icons/fi';
import { useTenant } from '../../context/TenantContext';
import { tenantDataService } from '../../services/tenantDataService';
import { invoiceService } from '../../services/invoiceService';
import toast from 'react-hot-toast';
import PayrollReview from './PayrollReview';
import { VoidInvoiceModal, SupersedeInvoiceModal } from './InvoiceStatusModals';
import InvoicePreviewModal from '../billing/InvoicePreviewModal';
import RevenueTrendsChart from '../charts/RevenueTrendsChart';

export default function FinancialDashboard() {
  const { currentTenant } = useTenant();
  const [activeTab, setActiveTab] = useState('overview');
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: 'month',
    status: 'all',
    clientName: '',
    staffName: '',
    treatmentType: ''
  });
  const [chartType, setChartType] = useState('line');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailingInvoices, setEmailingInvoices] = useState(new Set());

  useEffect(() => {
    loadFinancialData();
  }, [currentTenant]);

  const loadFinancialData = async () => {
    if (!currentTenant?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Load tenant-scoped invoices and expenses
      tenantDataService.setTenantContext(currentTenant.id);
      const [invoicesData, expensesData] = await Promise.all([
        tenantDataService.getAll('invoices'),
        tenantDataService.getAll('expenses')
      ]);
      
      setInvoices(invoicesData || []);
      setExpenses(expensesData || []);
    } catch (error) {
      console.error('Error loading financial data:', error);
      toast.error('Failed to load financial data');
      setInvoices([]);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    const totalRevenue = parseFloat(invoices
      .filter(inv => inv.status === 'paid')
      .reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0).toFixed(2));

    const totalExpenses = parseFloat(expenses
      .reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0).toFixed(2));

    const pendingRevenue = parseFloat(invoices
      .filter(inv => inv.status === 'unpaid')
      .reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0).toFixed(2));

    return { totalRevenue, totalExpenses, pendingRevenue };
  };

  const { totalRevenue, totalExpenses, pendingRevenue } = calculateTotals();

  const tabs = [
    { key: 'overview', label: 'Overview', icon: FiTrendingUp },
    { key: 'invoices', label: 'Invoices', icon: FiFileText },
    { key: 'expenses', label: 'Expenses', icon: FiDollarSign },
    { key: 'payroll', label: 'Payroll', icon: FiUsers }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-600">Loading financial data...</div>
      </div>
    );
  }

  if (!currentTenant) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-600">No clinic selected</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Financial Dashboard</h2>
          <div className="text-sm text-gray-600 mt-1">
            Financial data for: <strong>{currentTenant.name}</strong>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowExpenseModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FiPlus size={16} />
            <span>Add Expense</span>
          </button>
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <FiDownload size={16} />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <Icon size={16} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-600">£{totalRevenue.toFixed(2)}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <FiTrendingUp className="text-green-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Expenses</p>
                  <p className="text-2xl font-bold text-red-600">£{totalExpenses.toFixed(2)}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <FiDollarSign className="text-red-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pending Revenue</p>
                  <p className="text-2xl font-bold text-orange-600">£{pendingRevenue.toFixed(2)}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <FiFileText className="text-orange-600" size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* Revenue Trends Chart */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Revenue Trends</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setChartType('line')}
                  className={`p-2 rounded-lg transition-colors ${
                    chartType === 'line' 
                      ? 'bg-blue-100 text-blue-600' 
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                  title="Line Chart"
                >
                  <FiTrendingUp size={16} />
                </button>
                <button
                  onClick={() => setChartType('bar')}
                  className={`p-2 rounded-lg transition-colors ${
                    chartType === 'bar' 
                      ? 'bg-blue-100 text-blue-600' 
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                  title="Bar Chart"
                >
                  <FiBarChart size={16} />
                </button>
              </div>
            </div>
            <RevenueTrendsChart invoices={invoices} chartType={chartType} />
          </div>
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <InvoicesTab
          invoices={invoices}
          currentTenant={currentTenant}
          filters={filters}
          setFilters={setFilters}
          onRefresh={loadFinancialData}
        />
      )}

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <ExpensesTab
          expenses={expenses}
          onRefresh={loadFinancialData}
        />
      )}

      {/* Payroll Tab */}
      {activeTab === 'payroll' && (
        <PayrollTab />
      )}

      {/* Modals */}
      {showExpenseModal && (
        <ExpenseModal
          onClose={() => setShowExpenseModal(false)}
          onSave={loadFinancialData}
        />
      )}
    </div>
  );
}

// Invoices Tab Component
function InvoicesTab({ invoices, filters, setFilters, onRefresh, currentTenant }) {
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [showSupersedeModal, setShowSupersedeModal] = useState(false);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [supersedeReason, setSupersedeReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailingInvoices, setEmailingInvoices] = useState(new Set());

  const handleVoidInvoice = async () => {
    setLoading(true);
    try {
      await invoiceService.voidInvoice(selectedInvoice.id, voidReason);
      toast.success('Invoice voided successfully');
      setShowVoidModal(false);
      setVoidReason('');
      setSelectedInvoice(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to void invoice');
      console.error('Error voiding invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSupersedeInvoice = async () => {
    setLoading(true);
    try {
      await invoiceService.supersedeInvoice(selectedInvoice.id, '', supersedeReason);
      toast.success('Invoice superseded successfully');
      setShowSupersedeModal(false);
      setSupersedeReason('');
      setSelectedInvoice(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to supersede invoice');
      console.error('Error superseding invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoicePreview(true);
  };

  const handleEmailInvoice = async (invoice) => {
    if (emailingInvoices.has(invoice.id)) {
      toast.error('Email is already being sent for this invoice');
      return;
    }

    setEmailingInvoices(prev => new Set(prev).add(invoice.id));
    try {
      // Prepare client data
      const clientData = {
        email: invoice.clientEmail,
        name: invoice.clientName
      };

      // Prepare tenant data
      const tenantData = {
        id: currentTenant?.id,
        name: currentTenant?.name || 'Healthcare Clinic',
        email: currentTenant?.email || '',
        address: currentTenant?.address || '',
        phone: currentTenant?.phone || ''
      };

      await invoiceService.sendInvoiceEmail(invoice.id, clientData, tenantData);
      toast.success(`Invoice ${invoice.invoiceNumber} sent to ${invoice.clientName}!`);
      onRefresh(); // Refresh to show email sent indicator
    } catch (error) {
      console.error('Error sending invoice email:', error);
      if (error.message.includes('sent recently')) {
        toast.error('Email was sent recently. Please wait before sending again.');
      } else {
        toast.error(`Failed to send invoice email: ${error.message}`);
      }
    } finally {
      setEmailingInvoices(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoice.id);
        return newSet;
      });
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await invoiceService.markAsPaid(invoiceId);
      toast.success('Invoice marked as paid');
      onRefresh(); // Refresh data
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      toast.error('Failed to mark invoice as paid');
    }
  };

  const handleMarkUnpaid = async (invoiceId) => {
    try {
      await invoiceService.markAsUnpaid(invoiceId);
      toast.success('Invoice marked as unpaid');
      onRefresh(); // Refresh data
    } catch (error) {
      console.error('Error marking invoice as unpaid:', error);
      toast.error('Failed to mark invoice as unpaid');
    }
  };
  const filteredInvoices = invoices.filter(invoice => {
    if (filters.status !== 'all' && invoice.status !== filters.status) return false;
    if (filters.clientName && !invoice.clientName.toLowerCase().includes(filters.clientName.toLowerCase())) return false;
    return true;
  }).sort((a, b) => {
    // Extract numeric part from invoice numbers (e.g., "INV-0000006" -> 6)
    const getInvoiceNumber = (invoiceNum) => {
      if (!invoiceNum) return 0;
      const match = invoiceNum.match(/(\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    };
    
    const aNum = getInvoiceNumber(a.invoiceNumber);
    const bNum = getInvoiceNumber(b.invoiceNumber);
    
    // Sort by invoice number descending (highest first)
    return bNum - aNum;
  });

  return (
    <div className="space-y-4">
      {/* Enhanced Filters */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="flex flex-wrap gap-6">
          <div className="min-w-[160px]">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value="all">All Invoices</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="void">Void</option>
              <option value="superseded">Superseded</option>
            </select>
          </div>
          <div className="min-w-[200px] flex-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Client Name</label>
            <input
              type="text"
              value={filters.clientName}
              onChange={(e) => setFilters(prev => ({ ...prev, clientName: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Search by client name..."
            />
          </div>
        </div>
      </div>

      {/* Enhanced Invoices Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <h3 className="text-lg font-semibold text-gray-900">Invoices</h3>
          <p className="text-sm text-gray-600 mt-1">{filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''} found</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Invoice #</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Client</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-50">
              {filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <FiFileText className="w-12 h-12 text-gray-300 mb-4" />
                      <p className="text-lg font-medium">No invoices found</p>
                      <p className="text-sm">Try adjusting your filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice, index) => (
                  <tr key={invoice.id} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900">{invoice.invoiceNumber}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{invoice.clientName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">
                        {invoice.date?.seconds ? new Date(invoice.date.seconds * 1000).toLocaleDateString() : 
                         invoice.date && !isNaN(new Date(invoice.date)) ? new Date(invoice.date).toLocaleDateString() : 
                         invoice.createdAt?.seconds ? new Date(invoice.createdAt.seconds * 1000).toLocaleDateString() :
                         invoice.createdAt && !isNaN(new Date(invoice.createdAt)) ? new Date(invoice.createdAt).toLocaleDateString() :
                         'No Date'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900">£{parseFloat(invoice.total || 0).toFixed(2)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                        invoice.status === 'paid' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' :
                        invoice.status === 'unpaid' ? 'bg-red-100 text-red-800 border border-red-200' :
                        invoice.status === 'void' ? 'bg-gray-100 text-gray-800 border border-gray-200' :
                        invoice.status === 'superseded' ? 'bg-amber-100 text-amber-800 border border-amber-200' :
                        'bg-gray-100 text-gray-800 border border-gray-200'
                      }`}>
                        {invoice.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEmailInvoice(invoice)}
                          disabled={emailingInvoices.has(invoice.id)}
                          className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            emailingInvoices.has(invoice.id) 
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                          title={invoice.lastEmailSent ? `Last sent: ${new Date(invoice.lastEmailSent.seconds ? invoice.lastEmailSent.seconds * 1000 : invoice.lastEmailSent).toLocaleString()}` : "Email invoice to client"}
                        >
                          {emailingInvoices.has(invoice.id) ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400 mr-1"></div>
                          ) : (
                            <>
                              <FiMail className="w-3 h-3 mr-1" />
                              {invoice.lastEmailSent && (
                                <span className="w-2 h-2 bg-green-500 rounded-full mr-1" title="Email sent"></span>
                              )}
                            </>
                          )}
                          {emailingInvoices.has(invoice.id) ? 'Sending...' : 'Email'}
                        </button>
                        {invoice.status === "unpaid" && (
                          <button
                            onClick={() => handleMarkPaid(invoice.id)}
                            className="inline-flex items-center px-3 py-1.5 bg-emerald-100 rounded-lg text-emerald-700 hover:bg-emerald-200 text-xs font-medium transition-colors"
                          >
                            Mark Paid
                          </button>
                        )}
                        {invoice.status === "paid" && (
                          <button
                            onClick={() => handleMarkUnpaid(invoice.id)}
                            className="inline-flex items-center px-3 py-1.5 bg-amber-100 rounded-lg text-amber-700 hover:bg-amber-200 text-xs font-medium transition-colors"
                          >
                            Mark Unpaid
                          </button>
                        )}
                        {(invoice.status === "paid" || invoice.status === "unpaid") && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedInvoice(invoice);
                                setShowVoidModal(true);
                              }}
                              className="inline-flex items-center px-3 py-1.5 bg-red-100 rounded-lg text-red-700 hover:bg-red-200 text-xs font-medium transition-colors"
                              title="Void invoice"
                            >
                              <FiX className="w-3 h-3 mr-1" />
                              Void
                            </button>
                            <button
                              onClick={() => {
                                setSelectedInvoice(invoice);
                                setShowSupersedeModal(true);
                              }}
                              className="inline-flex items-center px-3 py-1.5 bg-amber-100 rounded-lg text-amber-700 hover:bg-amber-200 text-xs font-medium transition-colors"
                              title="Supersede invoice"
                            >
                              <FiAlertTriangle className="w-3 h-3 mr-1" />
                              Supersede
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleViewInvoice(invoice)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                        >
                          <FiEye className="w-3 h-3 mr-1" />
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <VoidInvoiceModal
        isOpen={showVoidModal}
        invoice={selectedInvoice}
        voidReason={voidReason}
        setVoidReason={setVoidReason}
        onVoid={handleVoidInvoice}
        onClose={() => {
          setShowVoidModal(false);
          setSelectedInvoice(null);
          setVoidReason('');
        }}
      />

      <SupersedeInvoiceModal
        isOpen={showSupersedeModal}
        invoice={selectedInvoice}
        supersedeReason={supersedeReason}
        setSupersedeReason={setSupersedeReason}
        onSupersede={handleSupersedeInvoice}
        onClose={() => {
          setShowSupersedeModal(false);
          setSelectedInvoice(null);
          setSupersedeReason('');
        }}
      />

      {/* Invoice Preview Modal */}
      {showInvoicePreview && selectedInvoice && (
        <InvoicePreviewModal
          bill={selectedInvoice}
          client={{ name: selectedInvoice.clientName, email: selectedInvoice.clientEmail }}
          appointment={null}
          onClose={() => {
            setShowInvoicePreview(false);
            setSelectedInvoice(null);
          }}
          onRefresh={onRefresh}
          userRole="admin"
        />
      )}
    </div>
  );
}

// Expenses Tab Component
function ExpensesTab({ expenses, onRefresh }) {
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payee</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {expenses.map((expense) => (
              <tr key={expense.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {expense.date?.seconds ? new Date(expense.date.seconds * 1000).toLocaleDateString() : new Date(expense.date).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {expense.payee}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {expense.category}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  £{parseFloat(expense.amount || 0).toFixed(2)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {expense.description}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Payroll Tab Component
function PayrollTab() {
  return (
    <div className="space-y-4">
      <PayrollReview />
    </div>
  );
}

// Expense Modal Component
function ExpenseModal({ onClose, onSave }) {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    payee: '',
    category: 'Equipment',
    amount: '',
    description: '',
    receipt: null
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const expenses = JSON.parse(localStorage.getItem('expenses') || '[]');
    const newExpense = {
      ...formData,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };
    expenses.push(newExpense);
    localStorage.setItem('expenses', JSON.stringify(expenses));
    onSave();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Expense</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payee</label>
            <input
              type="text"
              value={formData.payee}
              onChange={(e) => setFormData(prev => ({ ...prev, payee: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="Equipment">Equipment</option>
              <option value="Rent">Rent</option>
              <option value="Travel">Travel</option>
              <option value="Training">Training</option>
              <option value="Misc">Miscellaneous</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (£)</label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              rows={3}
            />
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Add Expense
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
